/**
 * PLAT-11445 — one place that decides which media a clip plays, and how long
 * it is, given the story's current state. Every clip surface (Clip Editor,
 * Creator Studio via lightweight-clip expansion, producer previews, the
 * lightweight save) resolves through here at OPEN time instead of baking a
 * URL at save time, so a clip saved while the story's video renditions were
 * still producing heals itself once they land.
 *
 * Precedence while renditions are pending: a session-local video the caller
 * holds → the story's editor audio (audio-first, black canvas) → nothing.
 * Once a preview rendition exists it always wins. The unprocessed original is
 * only a fallback when renditions are *permanently* absent (legacy/failed) —
 * never while pending (Leg 1 lock: no silent fallback to the multi-GB
 * original).
 *
 * Duration follows the same idea: the probed story duration when the pipeline
 * has produced it, else the transcript's end, else a caller-supplied audio
 * probe. Pure function — probes and file lookups belong to the callers.
 */
import { isCompressedRenditionPending } from './storyReadiness';

import type { RenditionReadinessCarrier } from './storyReadiness';

export type ClipMediaKind =
  | 'rendition'
  | 'local'
  | 'audio-first'
  | 'original'
  | 'none';

export interface ClipMediaResolution {
  /** Playable URL for the clip's media, or null when nothing is playable yet. */
  url: string | null;
  kind: ClipMediaKind;
  /** Best-known media length in seconds, or null when nothing can say yet. */
  durationSeconds: number | null;
  renditionPending: boolean;
}

export interface ClipMediaVideo {
  duration?: number | null;
  mp4Url?: string | null;
  mp4UrlLow?: string | null;
  mp4UrlMedium?: string | null;
  mp4UrlHigh?: string | null;
  mp4UrlHd?: string | null;
}

export interface ClipMediaStory extends RenditionReadinessCarrier {
  originalVideo?: {
    url?: string | null;
    video?: ClipMediaVideo | null;
  } | null;
  transcription?: { audio?: { url?: string | null } | null } | null;
}

export interface ResolveClipMediaOptions {
  /**
   * A video URL only this session can play (object URL of the uploaded File,
   * or the renderer's synthetic local-canvas URL). Used while pending.
   */
  localVideoUrl?: string | null;
  /** End of the last transcript element, in seconds. */
  transcriptEndSeconds?: number | null;
  /** Probed length of the editor audio, in seconds. */
  audioDurationSeconds?: number | null;
}

function positive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** The preview rendition to edit against, smallest first. */
export function pickRenditionUrl(
  video: ClipMediaVideo | null | undefined,
): string | null {
  if (!video) return null;
  return (
    nonEmpty(video.mp4UrlLow) ??
    nonEmpty(video.mp4UrlMedium) ??
    nonEmpty(video.mp4UrlHigh) ??
    nonEmpty(video.mp4UrlHd)
  );
}

export function resolveClipMediaDuration(
  story: ClipMediaStory | null | undefined,
  opts: ResolveClipMediaOptions = {},
): number | null {
  return (
    positive(story?.originalVideo?.video?.duration) ??
    positive(opts.transcriptEndSeconds) ??
    positive(opts.audioDurationSeconds)
  );
}

export function resolveClipMediaSource(
  story: ClipMediaStory | null | undefined,
  opts: ResolveClipMediaOptions = {},
): ClipMediaResolution {
  const renditionPending = isCompressedRenditionPending(story);
  const durationSeconds = resolveClipMediaDuration(story, opts);
  const video = story?.originalVideo?.video;

  const rendition = pickRenditionUrl(video);
  if (rendition) {
    return {
      url: rendition,
      kind: 'rendition',
      durationSeconds,
      renditionPending,
    };
  }

  if (renditionPending) {
    const local = nonEmpty(opts.localVideoUrl);
    if (local) {
      return { url: local, kind: 'local', durationSeconds, renditionPending };
    }
    const audio = nonEmpty(story?.transcription?.audio?.url);
    if (audio) {
      return {
        url: audio,
        kind: 'audio-first',
        durationSeconds,
        renditionPending,
      };
    }
    return { url: null, kind: 'none', durationSeconds, renditionPending };
  }

  const original =
    nonEmpty(video?.mp4Url) ?? nonEmpty(story?.originalVideo?.url);
  if (original) {
    return {
      url: original,
      kind: 'original',
      durationSeconds,
      renditionPending,
    };
  }
  return { url: null, kind: 'none', durationSeconds, renditionPending };
}

/** End of the last timed transcript element, or null. */
export function transcriptEndSeconds(
  elements:
    | ReadonlyArray<{ end_ts?: number | null } | null | undefined>
    | null
    | undefined,
): number | null {
  if (!Array.isArray(elements)) return null;
  let end: number | null = null;
  for (const el of elements) {
    const candidate = positive(el?.end_ts);
    if (candidate != null && (end == null || candidate > end)) end = candidate;
  }
  return end;
}
